/**
 * Basket store — tracks the active merchandise basket ID and item count
 * for the persistent BasketIcon in the navigation header.
 *
 * On login the store hydrates from the API (`GET /api/baskets/active?source=merchandise`)
 * so the basket syncs across devices and browser sessions.
 * localStorage acts as a fast cache between page reloads; the API is the source of truth.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getActiveBasket } from '@/services/basket';

interface BasketState {
  /** The current active basket ID (null if none) */
  basketId: string | null;
  /** Cached item count for the nav badge */
  itemCount: number;

  // Actions
  setBasket: (basketId: string, itemCount: number) => void;
  updateItemCount: (count: number) => void;
  clearBasket: () => void;
  /** Hydrate basket state from the API — call once after authentication */
  hydrateFromApi: () => Promise<void>;
}

export const useBasketStore = create<BasketState>()(
  persist(
    (set) => ({
      basketId: null,
      itemCount: 0,

      setBasket: (basketId, itemCount) => set({ basketId, itemCount }),
      updateItemCount: (count) => set({ itemCount: count }),
      clearBasket: () => set({ basketId: null, itemCount: 0 }),

      hydrateFromApi: async () => {
        try {
          const basket = await getActiveBasket('merchandise');
          if (basket && basket.id) {
            const count = basket.lines?.reduce((sum, l) => sum + l.quantity, 0) ?? 0;
            set({ basketId: basket.id, itemCount: count });
          }
          // If no active Draft basket found, do NOT clear the store.
          // The basket may be temporarily in Processing/Processed status
          // (e.g. user is mid-checkout or just cancelled Stripe payment).
          // The store is only cleared explicitly when payment is confirmed
          // (OrderSummaryPage / PaymentConfirmingPage) or on logout.
        } catch {
          // Non-critical — keep whatever localStorage has
        }
      },
    }),
    {
      name: 'pce-basket-storage',
      // Only persist the basket ID and count — not actions
      partialize: (state) => ({
        basketId: state.basketId,
        itemCount: state.itemCount,
      }),
    },
  ),
);
